import React, { useCallback, useEffect } from 'react';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

interface GoogleSignInButtonProps {
  onSuccess: (response: any) => void;
  onError: (error: string) => void;
}

declare global {
  interface Window {
    google: any;
  }
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({ onSuccess, onError }) => {
  
  const handleGoogleResponse = useCallback((response: any) => {
    if (response.credential) {
      try {
        const userInfo = JSON.parse(atob(response.credential.split('.')[1]));
        onSuccess({ credential: response.credential, user: userInfo });
      } catch (error) {
        onError('Failed to process Google response');
      }
    } else {
      onError('Google Sign-In failed');
    }
  }, [onSuccess, onError]);

  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (window.google) {
        try {
          // Initialize without FedCM-related options
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleGoogleResponse,
            auto_select: false,
            cancel_on_tap_outside: false,
          });
        } catch (initError) {
          console.error('Google Sign-In initialization failed:', initError);
        }
      }
    };

    const checkGoogleSDK = () => {
      if (window.google) {
        initializeGoogleSignIn();
      } else {
        setTimeout(checkGoogleSDK, 100);
      }
    };

    checkGoogleSDK();
  }, [handleGoogleResponse]); // Added dependency

  const handleClick = () => {
    console.log('Google Sign-In button clicked');
    console.log('Current origin:', window.location.origin);
    
    if (window.google && window.google.accounts && window.google.accounts.id) {
      try {
        // Completely bypass FedCM and use classic popup
        // Reinitialize to ensure clean state
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: false,
        });
        
        // Create a hidden button and click it to trigger the popup
        const hiddenDiv = document.createElement('div');
        hiddenDiv.style.position = 'absolute';
        hiddenDiv.style.left = '-9999px';
        hiddenDiv.style.top = '-9999px';
        hiddenDiv.style.visibility = 'hidden';
        document.body.appendChild(hiddenDiv);
        
        // Render the official Google button
        window.google.accounts.id.renderButton(hiddenDiv, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          text: 'signin_with'
        });
        
        // Click the hidden button to trigger popup
        setTimeout(() => {
          const button = hiddenDiv.querySelector('div[role="button"]');
          if (button) {
            (button as HTMLElement).click();
          }
          
          // Clean up after a delay
          setTimeout(() => {
            if (document.body.contains(hiddenDiv)) {
              document.body.removeChild(hiddenDiv);
            }
          }, 1000);
        }, 100);
        
      } catch (error) {
        console.error('Google Sign-In failed:', error);
        onError('Google Sign-In failed. Please try refreshing the page.');
      }
    } else {
      onError('Google SDK not loaded. Please refresh the page.');
    }
  };

  return (
    <button
      className="nf-btn nf-btn-secondary nf-form-btn"
      style={{ 
        width: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        minWidth: 'unset',
        maxWidth: 'none'
      }}
      onClick={handleClick}
    >
      <span style={{ marginRight: '0.5rem' }}>
        <img src="/google-logo.svg" alt="Google" style={{ width: 20, height: 20 }} />
      </span>
      Sign in with Google
    </button>
  );
};

export default GoogleSignInButton;
